import { useState, useEffect } from 'react';
import Button from '../base/Button';

interface BookingCalendarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BookingCalendar({ isOpen, onClose }: BookingCalendarProps) {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availability, setAvailability] = useState<Record<string, string[]> | null>(null);
  const [availabilityReload, setAvailabilityReload] = useState(0);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefono: '',
    tipo_consulenza: 'gratuita',
    note: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Genera le date disponibili per i prossimi 30 giorni (esclusi weekend)
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Escludi sabato (6) e domenica (0)
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        dates.push({
          value: date.toISOString().split('T')[0],
          label: date.toLocaleDateString('it-IT', { 
            weekday: 'long', 
            day: 'numeric', 
            month: 'long' 
          })
        });
      }
    }
    
    return dates.slice(0, 15); // Mostra solo i primi 15 giorni lavorativi
  };

  const baseSlotsFor = (date: Date) => {
    const slots: string[] = [];
    const start = new Date(date);
    start.setHours(8, 0, 0, 0);
    const end = new Date(date);
    end.setHours(20, 0, 0, 0);
    const d = new Date(start);
    while (d < end) {
      const hh = d.getHours().toString().padStart(2, '0');
      const mm = d.getMinutes().toString().padStart(2, '0');
      slots.push(`${hh}:${mm}`);
      d.setMinutes(d.getMinutes() + 20);
    }
    return slots;
  };

  useEffect(() => {
    const loadGoogleAvailability = async () => {
      if (!isOpen) return;
      if (!__GOOGLE_API_KEY__ || !__GOOGLE_CALENDAR_ID__) {
        console.warn('Google Calendar: API key or Calendar ID missing');
        return;
      }
      try {
        const timeMin = new Date();
        timeMin.setDate(timeMin.getDate() + 1);
        timeMin.setHours(0, 0, 0, 0);
        const timeMax = new Date();
        timeMax.setDate(timeMax.getDate() + 30);
        const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(__GOOGLE_CALENDAR_ID__)}/events?key=${__GOOGLE_API_KEY__}&timeMin=${timeMin.toISOString()}&timeMax=${timeMax.toISOString()}&singleEvents=true`;
        console.log('Google Calendar fetch URL', url);
        const res = await fetch(url, { headers: { accept: 'application/json' } });
        if (!res.ok) return;
        const data = await res.json();
        console.log('Google Calendar response items count', Array.isArray(data.items) ? data.items.length : 0);
        const items = Array.isArray(data.items) ? data.items : [];
        const busyByDate: Record<string, Array<{ start: Date; end: Date; allDay?: boolean }>> = {};
        for (const ev of items) {
          const startStr = ev?.start?.dateTime || ev?.start?.date;
          const endStr = ev?.end?.dateTime || ev?.end?.date;
          if (!startStr || !endStr) continue;
          const isAllDay = !!ev?.start?.date && !ev?.start?.dateTime;
          const start = new Date(startStr);
          const end = new Date(endStr);
          const dstr = start.toISOString().split('T')[0];
          (busyByDate[dstr] ||= []).push({ start, end, allDay: isAllDay });
        }

        const map: Record<string, string[]> = {};
        const today = new Date();
        for (let i = 1; i <= 30; i++) {
          const date = new Date(today);
          date.setDate(today.getDate() + i);
          if (date.getDay() === 0 || date.getDay() === 6) continue;
          const dstr = date.toISOString().split('T')[0];
          const intervals = busyByDate[dstr] || [];
          if (intervals.some((b) => b.allDay)) {
            map[dstr] = [];
            continue;
          }
          const slots = baseSlotsFor(date).filter((t) => {
            const [hh, mm] = t.split(':').map((v) => parseInt(v));
            const slot = new Date(date);
            slot.setHours(hh, mm, 0, 0);
            return !intervals.some(({ start, end }) => slot >= start && slot < end);
          });
          map[dstr] = slots;
        }
        if (Object.keys(map).length) setAvailability(map);
      } catch (e) { console.error('Google Calendar fetch error', e); }
    };
    loadGoogleAvailability();
  }, [isOpen, availabilityReload]);

  useEffect(() => {
    const loadAvailability = async () => {
      if (!__AVAILABILITY_URL__ || !isOpen) return;
      if (__GOOGLE_API_KEY__ && __GOOGLE_CALENDAR_ID__) return;
      try {
        const res = await fetch(__AVAILABILITY_URL__, { headers: { accept: 'application/json' } });
        if (!res.ok) return;
        const data = await res.json();
        let map: Record<string, string[]> = {};
        if (Array.isArray(data)) {
          data.forEach((d: any) => {
            if (d && d.date && Array.isArray(d.times)) map[d.date] = d.times;
          });
        } else if (data && Array.isArray(data.dates)) {
          data.dates.forEach((d: any) => {
            if (d && d.date && Array.isArray(d.times)) map[d.date] = d.times;
          });
        } else if (data && typeof data === 'object') {
          map = data as Record<string, string[]>;
        }
        if (Object.keys(map).length) setAvailability(map);
      } catch {}
    };
    loadAvailability();
  }, [isOpen]);

  const generateAvailableDatesRemote = () => {
    if (!availability) return [] as { value: string; label: string }[];
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0,0,0,0);
    return Object.keys(availability)
      .sort()
      .filter((value) => {
        const d = new Date(value);
        return d >= tomorrow;
      })
      .map((value) => {
        const [y, m, d] = value.split('-').map((v) => parseInt(v));
        const date = new Date(y, (m || 1) - 1, d || 1);
        return {
          value,
          label: date.toLocaleDateString('it-IT', {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
          })
        };
      });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    if (!selectedDate || !selectedTime) {
      alert('Seleziona data e orario per la videoconferenza');
      return;
    }

    setIsSubmitting(true);
    
    try {
      let bookingOk = true;
      let meetLink: string | null = null;
      if (__BOOKING_API_URL__) {
        const start = new Date(selectedDate);
        const [hh, mm] = selectedTime.split(':').map((v) => parseInt(v));
        start.setHours(hh, mm, 0, 0);
        const end = new Date(start);
        end.setMinutes(end.getMinutes() + 20);
        try {
          const qs = new URLSearchParams({
            calendarId: __GOOGLE_CALENDAR_ID__,
            start: start.toISOString(),
            end: end.toISOString(),
            summary: 'Videoconferenza',
            name: formData.nome,
            email: formData.email,
            phone: formData.telefono,
            type: formData.tipo_consulenza,
            notes: formData.note,
          });
          await fetch(`${__BOOKING_API_URL__}?${qs.toString()}`, {
            method: 'GET',
            mode: 'no-cors',
          });
          bookingOk = true;
        } catch {
          bookingOk = false;
        }

        if (bookingOk && __GOOGLE_API_KEY__ && __GOOGLE_CALENDAR_ID__) {
          let verified = false;
          for (let i = 0; i < 3; i++) {
            await new Promise((r) => setTimeout(r, 800));
            const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(__GOOGLE_CALENDAR_ID__)}/events?key=${__GOOGLE_API_KEY__}&timeMin=${start.toISOString()}&timeMax=${end.toISOString()}&singleEvents=true&fields=items(id,hangoutLink,conferenceData(entryPoints(uri,entryPointType)))`;
            try {
              const res = await fetch(url, { headers: { accept: 'application/json' } });
              if (res.ok) {
                const data = await res.json();
                const items = Array.isArray(data.items) ? data.items : [];
                if (items.length > 0) {
                  verified = true;
                  const ev = items[0];
                  if (ev?.conferenceData?.entryPoints) {
                    const ep = ev.conferenceData.entryPoints.find((e: any) => e.entryPointType === 'video');
                    if (ep?.uri) meetLink = ep.uri;
                  }
                  if (!meetLink && ev?.hangoutLink) meetLink = ev.hangoutLink;
                  break;
                }
              }
            } catch {}
          }
          bookingOk = verified;
        }
      }

      if (bookingOk) {
        const d = new Date(selectedDate);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        const formattedDate = `${dd}/${mm}/${yyyy}`;
        const subject = `Nuova prenotazione - ${formattedDate} ${selectedTime}`;
        const body = `Prenotazione confermata\n\n` +
          `Data: ${formattedDate}\n` +
          `Orario: ${selectedTime}\n` +
          `Nome: ${formData.nome}\n` +
          `Email: ${formData.email}\n` +
          `Telefono: ${formData.telefono}\n` +
          `Note: ${formData.note}` + (meetLink ? `\nLink Meet: ${meetLink}\n` : '');
        const html = `<!DOCTYPE html><html><head><meta charset=\"UTF-8\"><meta name=\"viewport\" content=\"width=device-width,initial-scale=1\"><title>Prenotazione Confermata</title></head><body style=\"margin:0;padding:0;background:#f6f8fb;font-family:Arial,Helvetica,sans-serif;color:#1f2937;\">\n          <table role=\"presentation\" width=\"100%\" cellspacing=\"0\" cellpadding=\"0\" style=\"background:#f6f8fb;padding:24px 0;\">\n            <tr><td align=\"center\">\n              <table role=\"presentation\" width=\"600\" cellspacing=\"0\" cellpadding=\"0\" style=\"max-width:600px;width:100%;background:#ffffff;border-radius:12px;box-shadow:0 6px 24px rgba(31,41,55,0.08);overflow:hidden;\">\n                <tr>\n                  <td style=\"background:#1e3a8a;padding:20px 24px;color:#ffffff;font-weight:bold;font-size:18px;\">\n                    AffittoOnline.it • Conferma Prenotazione\n                  </td>\n                </tr>\n                <tr>\n                  <td style=\"padding:24px 24px 8px 24px;\">\n                    <div style=\"font-size:16px;font-weight:600;margin-bottom:8px;color:#111827;\">Dettagli appuntamento</div>\n                    <table role=\"presentation\" width=\"100%\" cellspacing=\"0\" cellpadding=\"0\" style=\"border-collapse:collapse;\">\n                      <tr>\n                        <td style=\"padding:8px 0;color:#6b7280;width:160px;\">Data</td>\n                        <td style=\"padding:8px 0;color:#111827;\">${formattedDate}</td>\n                      </tr>\n                      <tr>\n                        <td style=\"padding:8px 0;color:#6b7280;width:160px;\">Orario</td>\n                        <td style=\"padding:8px 0;color:#111827;\">${selectedTime}</td>\n                      </tr>\n                      <tr>\n                        <td style=\"padding:8px 0;color:#6b7280;width:160px;\">Nome</td>\n                        <td style=\"padding:8px 0;color:#111827;\">${formData.nome}</td>\n                      </tr>\n                      <tr>\n                        <td style=\"padding:8px 0;color:#6b7280;width:160px;\">Email</td>\n                        <td style=\"padding:8px 0;color:#111827;\">${formData.email}</td>\n                      </tr>\n                      ${formData.telefono ? `<tr><td style=\"padding:8px 0;color:#6b7280;width:160px;\">Telefono</td><td style=\"padding:8px 0;color:#111827;\">${formData.telefono}</td></tr>` : ''}\n                      ${formData.note ? `<tr><td style=\"padding:8px 0;color:#6b7280;width:160px;\">Note</td><td style=\"padding:8px 0;color:#111827;\">${formData.note}</td></tr>` : ''}\n                    </table>\n                  </td>\n                </tr>\n                ${meetLink ? `<tr><td style=\"padding:8px 24px 0 24px;\">\n                  <a href=\"${meetLink}\" style=\"display:inline-block;background:#2563eb;color:#ffffff;text-decoration:none;padding:12px 16px;border-radius:8px;font-weight:600;\">Apri Google Meet</a>\n                </td></tr>` : ''}\n                <tr>\n                  <td style=\"padding:24px;color:#6b7280;font-size:12px;\">\n                    AffittoOnline.it — Contratti di Locazione in Videoconferenza\n                  </td>\n                </tr>\n              </table>\n            </td></tr>\n          </table>\n        </body></html>`;
        if (__MAIL_WEBHOOK_URL__) {
          try {
            const res = await fetch(__MAIL_WEBHOOK_URL__, {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({
                to: __GOOGLE_CALENDAR_ID__,
                subject,
                text: body,
                html,
              }),
            });
            const info: any = { status: res.status, ok: res.ok };
            try { info.data = await res.json(); } catch {}
            console.log('MAIL_WEBHOOK_RESULT', info);
            // invia copia anche al cliente
            try {
              const res2 = await fetch(__MAIL_WEBHOOK_URL__, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({
                  to: formData.email,
                  subject,
                  text: body,
                  html,
                }),
              });
              const info2: any = { status: res2.status, ok: res2.ok };
              try { info2.data = await res2.json(); } catch {}
              console.log('MAIL_WEBHOOK_RESULT_CUSTOMER', info2);
            } catch (e) {
              console.error('MAIL_WEBHOOK_ERROR_CUSTOMER', e);
            }
          } catch (e) {
            console.error('MAIL_WEBHOOK_ERROR', e);
          }
        } else if (__EMAILJS_SERVICE_ID__ && __EMAILJS_TEMPLATE_ID__ && __EMAILJS_PUBLIC_KEY__) {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Rome';
          try {
            await fetch('https://api.emailjs.com/api/v1.0/email/send', {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({
                service_id: __EMAILJS_SERVICE_ID__,
                template_id: __EMAILJS_TEMPLATE_ID__,
                user_id: __EMAILJS_PUBLIC_KEY__,
                template_params: {
                  to_email: __GOOGLE_CALENDAR_ID__,
                  subject,
                  message: body,
                  timezone: tz,
                },
              }),
            });
          } catch {}
        }
        setSubmitStatus('success');
        setAvailabilityReload((v) => v + 1);
        setFormData({
          nome: '',
          email: '',
          telefono: '',
          tipo_consulenza: 'gratuita',
          note: ''
        });
        setSelectedDate('');
        setSelectedTime('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">
            <i className="ri-calendar-check-fill text-blue-600 mr-2"></i>
            Prenota Videoconferenza Gratuita
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="p-6">
          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              <i className="ri-check-circle-fill mr-2"></i>
              Prenotazione confermata! Riceverai una email di conferma con il link per la videoconferenza.
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              <i className="ri-error-warning-fill mr-2"></i>
              Errore nella prenotazione. Riprova più tardi.
            </div>
          )}

          <form onSubmit={handleSubmit} data-readdy-form id="prenotazione-videoconferenza">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Selezione Data e Orario */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Seleziona Data e Orario
                </h3>
                {availability ? (
                  <p className="text-xs text-green-600 mb-2">
                    Disponibilità sincronizzata da Google Calendar
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mb-2">
                    Disponibilità locale (slot 20 minuti 08:00–20:00)
                  </p>
                )}
                
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Data Disponibile
                  </label>
                  <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {(availability ? generateAvailableDatesRemote() : generateAvailableDates()).map((date) => (
                      <label key={date.value} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="selectedDate"
                          value={date.value}
                          checked={selectedDate === date.value}
                          onChange={(e) => setSelectedDate(e.target.value)}
                          className="mr-3 text-blue-600"
                        />
                        <span className="text-sm capitalize">{date.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {selectedDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Orario Disponibile
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {(
                        availability && selectedDate
                          ? (availability[selectedDate] || [])
                          : (selectedDate ? baseSlotsFor(new Date(selectedDate)) : [])
                        ).map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => setSelectedTime(time)}
                          className={`p-2 text-sm rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${
                            selectedTime === time
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-600'
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Informazioni di Contatto */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Informazioni di Contatto
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Il tuo nome completo"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email per Videoconferenza *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="la-tua-email@esempio.it"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ti invieremo il link per la videoconferenza a questo indirizzo
                    </p>
                  </div>

                  <div>
                    <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefono
                    </label>
                    <input
                      type="tel"
                      id="telefono"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="+39 123 456 7890"
                    />
                  </div>

                  {/* Campo tipo consulenza rimosso */}

                  <div>
                    <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-2">
                      Note Aggiuntive
                    </label>
                    <textarea
                      id="note"
                      name="note"
                      value={formData.note}
                      onChange={handleInputChange}
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                      placeholder="Descrivi brevemente le tue esigenze..."
                    ></textarea>
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {formData.note.length}/500 caratteri
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row gap-4 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="cursor-pointer whitespace-nowrap"
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || submitStatus === 'success' || !selectedDate || !selectedTime}
                  className="cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Prenotazione...
                    </>
                  ) : (
                    <>
                      <i className="ri-calendar-check-fill mr-2"></i>
                      Conferma Prenotazione
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}